// frontend/src/components/mice/MiceTable.js
// Pure presentation component for the mice table.
// All state and action handlers are passed as props from MiceManagement.js.
// Wrapped in React.memo so it only re-renders when its specific props change,
// preventing unnecessary re-renders when unrelated parent state updates.

import React, { memo, useRef } from 'react';

const userColors = ['#D75B5B', '#6B8E23', '#F2A65B', '#8E7C4F', '#1F7A3D', '#1E3A3A', '#5F4B8B'];

function getUserColor(userId) {
  if (!userId) return '#CCCCCC';
  return userColors[userId % userColors.length];
}

export function calculateAge(dobString) {
  if (!dobString) return 'N/A';
  const dob = new Date(dobString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - dob.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const weeks = (diffDays / 7).toFixed(1);
  return `${weeks} wks`;
}

export function formatGenotype(genotypeArray) {
  if (!genotypeArray || genotypeArray.length === 0) return 'Not specified';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      {genotypeArray.map((g, idx) => (
        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', marginBottom: '2px' }}>
          {g.locus_name} ({g.allele_name})
          {g.color && (
            <span
              style={{ backgroundColor: g.color, width: '12px', height: '12px', borderRadius: '3px', marginLeft: '5px', border: '1px solid #ccc' }}
              title={g.color}
            />
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * Renders the mobile mouse card list.
 */
function MobileMiceList({ mice, selectedMiceForBulkMove, filtersMice, user, openMenuMouseId, onEdit, onMove, onDelete, onRestore, onViewPedigree, onOpenMenu }) {
  const lastTapRef = useRef({});

  const handleDoubleTap = (id, item) => {
    const now = Date.now();
    if (lastTapRef.current.id === id && now - lastTapRef.current.time < 300) {
      onEdit(item);
      lastTapRef.current = {};
    } else {
      lastTapRef.current = { id, time: now };
    }
  };

  return (
    <div className="mobile-list">
      {mice.map(mouse => (
        <div
          key={mouse.id}
          className="list-item"
          onClick={() => handleDoubleTap(mouse.id, mouse)}
          style={{ cursor: 'pointer', backgroundColor: selectedMiceForBulkMove.includes(mouse.id) ? 'rgba(0,123,255,0.1)' : 'transparent' }}
        >
          <div className="item-content">
            <div className="item-title">
              <p className="genotype-chip">
                {mouse.physical_tag ? `Mouse #${mouse.physical_tag}, ` : 'No #, '}
              </p>
              {mouse.genotype && mouse.genotype.length > 0
                ? mouse.genotype.map((g, idx) => (
                    <p key={idx} className="genotype-chip">{g.locus_name}({g.allele_name})</p>
                  ))
                : 'Wildtype'}
            </div>
            <div className="item-meta">
              {mouse.sex === 'Male' ? 'M' : 'F'} • {mouse.display_cage_number || 'No cage'} • {calculateAge(mouse.date_of_birth)}
            </div>
          </div>
          <div className="item-actions" onClick={(e) => e.stopPropagation()}>
            <button className="action-btn primary" onClick={() => onEdit(mouse)}>Edit</button>
            <div className="mouse-menu-wrapper">
              <button
                className="action-btn action-btn--menu"
                onClick={() => onOpenMenu(openMenuMouseId === mouse.id ? null : mouse.id)}
                title="More actions"
              >
                ...
              </button>
              {openMenuMouseId === mouse.id && (
                <div className="mouse-context-menu">
                  <button onClick={() => { onMove(mouse); onOpenMenu(null); }}>Move</button>
                  {filtersMice.status === 'Archived' ? (
                    <button style={{ color: '#28a745' }} onClick={() => { onRestore(mouse.id, mouse.physical_tag || mouse.mouse_id); onOpenMenu(null); }}>
                      Restore
                    </button>
                  ) : (
                    <button style={{ color: '#dc3545' }} onClick={() => { onDelete(mouse.id, mouse.physical_tag || mouse.mouse_id); onOpenMenu(null); }}>
                      Archive
                    </button>
                  )}
                  {user.subscription_tier !== 'free' && (
                    <button onClick={() => { onViewPedigree(mouse.id); onOpenMenu(null); }}>Pedigree</button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Renders the desktop mice table.
 */
function DesktopMiceTable({ mice, selectedMiceForBulkMove, setSelectedMiceForBulkMove, sortColumnMice, sortOrderMice, user, onSort, onSelectMouse, onDoubleClick, onViewPedigree }) {
  return (
    <table className="mice-table">
      <thead>
        <tr>
          <th style={{ width: '30px' }}>
            <input
              type="checkbox"
              onChange={(e) => {
                setSelectedMiceForBulkMove(e.target.checked ? mice.map(m => m.id) : []);
              }}
              checked={selectedMiceForBulkMove.length === mice.length && mice.length > 0}
              ref={input => {
                if (input) {
                  input.indeterminate = selectedMiceForBulkMove.length > 0 && selectedMiceForBulkMove.length < mice.length;
                }
              }}
            />
          </th>
          <th onClick={() => onSort('display_cage_number')}>Cage {sortColumnMice === 'display_cage_number' ? (sortOrderMice === 'asc' ? '▲' : '▼') : ''}</th>
          <th onClick={() => onSort('physical_tag')}>Tag {sortColumnMice === 'physical_tag' ? (sortOrderMice === 'asc' ? '▲' : '▼') : ''}</th>
          <th onClick={() => onSort('sex')}>Sex {sortColumnMice === 'sex' ? (sortOrderMice === 'asc' ? '▲' : '▼') : ''}</th>
          <th>Strain</th>
          <th>Genotype</th>
          <th onClick={() => onSort('date_of_birth')}>DOB {sortColumnMice === 'date_of_birth' ? (sortOrderMice === 'asc' ? '▲' : '▼') : ''}</th>
          <th onClick={() => onSort('age')}>Age {sortColumnMice === 'age' ? (sortOrderMice === 'asc' ? '▲' : '▼') : ''}</th>
          <th onClick={() => onSort('status')}>Status {sortColumnMice === 'status' ? (sortOrderMice === 'asc' ? '▲' : '▼') : ''}</th>
          <th>Notes</th>
          <th>Actions</th>
          <th>Owner</th>
        </tr>
      </thead>
      <tbody>
        {mice.map(mouse => (
          <tr
            key={mouse.id}
            onClick={() => onSelectMouse(mouse.id)}
            onDoubleClick={() => onDoubleClick(mouse)}
            style={{ cursor: 'pointer', backgroundColor: selectedMiceForBulkMove.includes(mouse.id) ? 'rgba(0,123,255,0.1)' : 'transparent' }}
          >
            <td onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={selectedMiceForBulkMove.includes(mouse.id)} onChange={() => onSelectMouse(mouse.id)} />
            </td>
            <td>{mouse.display_cage_number || 'N/A'}</td>
            <td>{mouse.physical_tag || ''}</td>
            <td>{mouse.sex === 'Male' ? 'M' : 'F'}</td>
            <td>{mouse.strain || ''}</td>
            <td>{formatGenotype(mouse.genotype)}</td>
            <td>{mouse.date_of_birth ? (() => { const d = new Date(mouse.date_of_birth); return `${String(d.getUTCMonth()+1).padStart(2,'0')}/${String(d.getUTCDate()).padStart(2,'0')}/${d.getUTCFullYear()}`; })() : 'N/A'}</td>
            <td>{calculateAge(mouse.date_of_birth)}</td>
            <td>{mouse.status}</td>
            <td style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mouse.notes || ''}</td>
            <td onClick={(e) => e.stopPropagation()}>
              {user.subscription_tier !== 'free' && (
                <button className="action-btn" onClick={() => onViewPedigree(mouse.id)}>Pedigree</button>
              )}
            </td>
            <td>
              <div
                style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: getUserColor(mouse.user_id), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
                title={mouse.owner_initials}
              >
                {mouse.owner_initials}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * MiceTable — switches between mobile list and desktop table based on `isMobile`.
 * Wrapped with React.memo to prevent re-renders when unrelated parent state changes.
 */
const MiceTable = memo(function MiceTable(props) {
  const { isMobile, ...rest } = props;
  return isMobile
    ? <MobileMiceList {...rest} />
    : <DesktopMiceTable {...rest} />;
});

export default MiceTable;
